import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchOrder, updateOrderStatus } from '../store/slices/orderSlice';
import Modal from '../components/common/Modal';
import './OrderDetails.css';
import { fmtMoney, fmtDate } from '../utils/format';
import conversationService from '../services/conversationService';

export default function OrderDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { order, isLoading } = useSelector(s => s.orders);
  const { user } = useSelector(s => s.auth);

  const [pending, setPending] = useState(null); // status value to confirm
  const [convOpen, setConvOpen] = useState(false);
  const [convSubject, setConvSubject] = useState('');

  useEffect(() => { if (id) dispatch(fetchOrder(id)); }, [dispatch, id]);

  const confirmChange = async () => {
    await dispatch(updateOrderStatus({ id, status: pending })).unwrap().catch(()=>{});
    setPending(null);
  };

  const billTo = order?.retailer || {};
  const billAddr = billTo?.address || {};
  const shipAddr = order?.shipping?.address || billAddr;
  const items = Array.isArray(order?.items) ? order.items : [];
  const currency = order?.amounts?.currency || 'USD';
  const total = order?.amounts?.total ?? order?.totalAmount;
  // salesRep removed per design update

  // No print-only filler; print mirrors the on-screen table

  const rows = useMemo(() => items.map((li, idx) => {
    const p = li.product || {};
    const item = p.sku || (p._id ? String(p._id).slice(-6) : '—');
    const desc = p.title || '—';
    const qty = li.qty ?? 0;
    const price = li.unitPrice ?? 0;
    const amount = (Number(qty) * Number(price)) || 0;
    return { key: li._id || idx, item, desc, qty, price, amount };
  }), [items]);

  // No print adjustment effect; keep print identical to screen table

  if (isLoading && !order) return <div>Loading...</div>;
  if (!order) return <div>Not found</div>;

  return (
    <div className="sales-order-page">
      <div className="actions">
        <button onClick={()=>navigate('/orders')}>Back</button>
        <button onClick={()=>navigate(`/orders/${id}/edit`)}>Edit</button>
        <button onClick={()=>{ setConvSubject(`Order #${order?.orderNumber || id} — Support`); setConvOpen(true); }}>Create support conversation</button>
        <select defaultValue="" onChange={(e)=>{ const v = e.target.value; e.target.value=''; if (!v) return; setPending(v); }}>
          <option value="" disabled>Change Status</option>
          {['Canceled','On Hold','Shipped','Pending'].map(s=> <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={()=>window.print()}>Print</button>
      </div>

      <div className="sales-order-header">
        <div className="sales-order-title">Sales Order</div>
        <div className="header-boxes">
          <div className="header-box">
            <label>Date</label>
            <div className="value">{fmtDate(order.orderDate)}</div>
          </div>
          <div className="header-box">
            <label>Sales Order #</label>
            <div className="value">{order.orderNumber || '-'}</div>
          </div>
        </div>
      </div>

      <div className="panels">
        <div className="panel-box">
          <h4>Bill To:</h4>
          <div>{billTo.companyName || '-'}</div>
          <div>{billAddr.line1 || billAddr.address1 || ''}</div>
          <div>{billAddr.line2 || ''}</div>
          <div>{[billAddr.city, billAddr.state, billAddr.postalCode].filter(Boolean).join(' ')}</div>
          <div>{billAddr.country || ''}</div>
        </div>
        <div className="panel-box">
          <h4>Ship To:</h4>
          <div>{shipAddr.name || billTo.companyName || '-'}</div>
          <div>{shipAddr.line1 || shipAddr.address1 || ''}</div>
          <div>{shipAddr.line2 || ''}</div>
          <div>{[shipAddr.city, shipAddr.state, shipAddr.postalCode].filter(Boolean).join(' ')}</div>
          <div>{shipAddr.country || ''}</div>
        </div>
      </div>

      <div className="info-strip">
        <div className="info-cell po">
          <label>Customer P.O. #</label>
          <div className="value">{order.customerPoNumber || order.poNumber || '-'}</div>
        </div>
        <div className="info-cell">
          <label>Payment Method</label>
          <div className="value">{order.paymentMethod || '-'}</div>
        </div>
        <div className="info-cell">
          <label>Ship Date</label>
          <div className="value">{fmtDate(order.shipDate)}</div>
        </div>
        <div className="info-cell shipvia">
          <label>Ship Via</label>
          <div className="value">{order.shipVia || '-'}</div>
        </div>
      </div>

      <div className="items-section">
  <table className="items-table">
          <thead>
            <tr>
              <th style={{width:'22%'}}>Item</th>
              <th>Description</th>
              <th style={{width:'12%'}}>Ordered</th>
              <th style={{width:'12%'}}>Price</th>
              <th style={{width:'14%'}}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key}>
                <td>{r.item}</td>
                <td>{r.desc}</td>
                <td className="num">{r.qty}</td>
                <td className="num">{fmtMoney(r.price, currency)}</td>
                <td className="num">{fmtMoney(r.amount, currency)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{textAlign:'center'}}>No line items</td></tr>
            )}
          </tbody>
          {/* Print-only total row inside the table footer to guarantee it stays with items */}
          <tfoot className="print-total-footer total-footer">
            <tr>
              <td colSpan={4} className="total-label">Total</td>
              <td className="num total-amount">{fmtMoney(total, currency)}</td>
            </tr>
          </tfoot>
        </table>
        {/* Total now lives inside the table footer so the table ends after Total */}
      </div>

      <Modal isOpen={!!pending} onClose={()=>setPending(null)}>
        <div style={{ padding: 12 }}>
          <h3>Confirm status change</h3>
          <p>Change status to <strong> {pending} </strong>?</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={confirmChange}>Confirm</button>
            <button onClick={()=>setPending(null)}>Cancel</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={convOpen} onClose={()=>setConvOpen(false)}>
        <div style={{ padding: 12 }}>
          <h3>Create support conversation</h3>
          <input style={{ width:'100%', padding:8, border:'1px solid #ddd', borderRadius:6 }} value={convSubject} onChange={e=>setConvSubject(e.target.value)} />
          <div style={{ display: 'flex', gap: 8, marginTop:12 }}>
            <button onClick={async ()=>{
              try {
                const subject = convSubject || `Order #${order?.orderNumber || id}`;
                const links = { orderId: order?._id || id, orderNumber: order?.orderNumber };
                let payload;
                if (user?.accountType === 'RTL') {
                  // Retailer creates with wholesalerId
                  payload = { subject, wholesalerId: order?.wholesaler?._id || order?.wholesaler, inbox: 'support', links, tags: ['order'] };
                } else {
                  // WHS/ADMIN creates with customerId
                  payload = { subject, customerId: order?.retailer?._id || order?.retailer, inbox: 'support', links, tags: ['order'] };
                }
                await conversationService.create(payload);
                setConvOpen(false);
                navigate(user?.accountType === 'RTL' ? '/messages' : '/support');
              } catch (e) { /* noop for now */ }
            }}>Create</button>
            <button onClick={()=>setConvOpen(false)}>Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
